import * as THREE from 'three'
import Producer from '../Producer.js'
import { device, onDeviceChange } from '../../shared/device.js'

// 觸控輕觸判定閾值：放開時與按下時的位移/時間都在門檻內才視為「點擊」而非拖曳/長按
const TAP_MAX_DIST = 10 // px
const TAP_MAX_DURATION = 500 // ms

/**
 * 3D 畫布互動橋接器：Raycaster (Hover / Click) + 滾動輸入正規化 (透過 Lenis)
 * 負責將 DOM 事件轉化為 Three.js 物件與消費端 (如 Carousel) 能用的狀態/callback
 * 注意：這裡消費的是 Producer 全域持有的 Lenis (this.producer.lenis)，只當事件正規化器用，
 * 不綁真實可捲動內容——Detail 的圖片畫廊是真的在捲動自己的 DOM，所以另外在 Detail.js
 * 自己管一顆綁在 .detail__gallery 上的 Lenis 實例，不經過這裡，生命週期也不歸 Producer 管
 */
export default class Interaction {
    constructor() {
        this.producer = new Producer()
        this.camera = this.producer.camera.instance
        this.viewport = this.producer.viewport

        // Mesh (THREE.Mesh) -> Owner (邏輯實例，如 Square) 的映射對照表
        this.targets = new Map()
        this._targetMeshes = []

        this.normalized = new THREE.Vector2()
        this.raycaster = new THREE.Raycaster()

        this.hovered = null
        this._isInsideWindow = true
        this._pointerDownInfo = null
        this._suppressNextClick = false

        // Hover 能力變化時 (如平板外接滑鼠)，主動清空當下的 Hover 狀態
        this._unsubscribeDevice = onDeviceChange((d) => {
            if (!d.hasHover) {
                this._clearHover()
            }
        })

        // 全域 Hover 監聽 (由 Director 設定)
        this.onHoverEnter = null
        this.onHoverLeave = null

        // 全域 Click / Scroll 監聽 (由 Carousel 設定)
        this.onClick = null
        this.onScroll = null

        // 訂閱 Producer 全域持有的 Lenis (生命週期歸 Producer 管，這裡只負責訂閱/取消訂閱)
        this._handleVirtualScroll = ({ deltaX, deltaY, event }) => {
            if (event.type === 'wheel') {
                // 阻擋 Mac 觸控板雙指橫向滑動時引發瀏覽器頁面上一頁/下一頁的預設行為
                event.preventDefault()
            }
            this.onScroll?.(deltaX, deltaY, event)
        }
        this.producer.lenis.on('virtual-scroll', this._handleVirtualScroll)

        // 綁定事件參照以利 destroy 移除
        this._onPointerMove = this._handlePointerMove.bind(this)
        this._onClick = this._handleClick.bind(this)
        this._onPointerDown = this._handlePointerDown.bind(this)
        this._onPointerUp = this._handlePointerUp.bind(this)
        this._onMouseEnter = () => {
            this._isInsideWindow = true
        }
        this._onMouseLeave = () => {
            this._isInsideWindow = false
            this._clearHover()
        }

        this._setEventListeners()
    }

    // ─────────────────────────────────────────────────────────────
    // Public API
    // ─────────────────────────────────────────────────────────────

    /** 註冊欲進行 Raycast 檢測的 3D Mesh 與其對應的 Owner 邏輯物件 */
    register(mesh, owner) {
        if (!mesh) {
            return
        }
        if (!this.targets.has(mesh)) {
            this._targetMeshes.push(mesh)
        }
        this.targets.set(mesh, owner)
    }

    /** 註銷 Mesh 的互動檢測 */
    unregister(mesh) {
        if (!this.targets.has(mesh)) {
            return
        }
        this.targets.delete(mesh)

        const idx = this._targetMeshes.indexOf(mesh)
        if (idx !== -1) {
            this._targetMeshes.splice(idx, 1)
        }

        // 若被註銷的 Mesh 正處於 Hover 狀態，主動清空
        if (this.hovered && this.targets.get(mesh) === this.hovered) {
            this._clearHover()
        }
    }

    /** 每幀由 Render Loop 驅動：在視窗內時持續更新 Hover 檢測 */
    update() {
        if (!this._isInsideWindow) {
            return
        }
        this._refreshHover()
    }

    /** 釋放所有全域事件監聽，防止記憶體洩漏 */
    destroy() {
        this._unsubscribeDevice()
        this.producer.lenis.off('virtual-scroll', this._handleVirtualScroll)
        document.removeEventListener('mouseenter', this._onMouseEnter)
        document.removeEventListener('mouseleave', this._onMouseLeave)
        window.removeEventListener('pointermove', this._onPointerMove)
        window.removeEventListener('click', this._onClick)
        window.removeEventListener('pointerdown', this._onPointerDown)
        window.removeEventListener('pointerup', this._onPointerUp)

        this.targets.clear()
        this._targetMeshes = []
        this.hovered = null
    }

    // ─────────────────────────────────────────────────────────────
    // Internal Raycast & Event Handlers
    // ─────────────────────────────────────────────────────────────

    /** 將螢幕座標 (clientX, clientY) 轉為 NDC 座標 (-1 ~ +1) */
    _updateNormalized(clientX, clientY) {
        this.viewport.screenToNDC(clientX, clientY, this.normalized)
    }

    /** 執行 Raycast 並回傳命中對象的 Owner 實例 */
    _raycastOwner() {
        if (this._targetMeshes.length === 0) {
            return null
        }

        this.raycaster.setFromCamera(this.normalized, this.camera)
        const hits = this.raycaster.intersectObjects(this._targetMeshes, false)

        if (hits.length === 0) {
            return null
        }
        return this.targets.get(hits[0].object) ?? null
    }

    /** 更新並觸發 Hover 狀態切換的回調：觸控裝置 (無穩定 Hover 能力) 一律跳過 */
    _refreshHover() {
        if (!device.hasHover) {
            return
        }

        const hit = this._raycastOwner()
        if (hit === this.hovered) {
            return
        }

        // 舊 Hover 對象 Leave
        if (this.hovered) {
            this.hovered.onHoverLeave?.()
            this.onHoverLeave?.(this.hovered)
        }

        // 新 Hover 對象 Enter
        if (hit) {
            hit.onHoverEnter?.()
            this.onHoverEnter?.(hit)
        }

        this.hovered = hit
    }

    /** 清空 Hover 狀態 */
    _clearHover() {
        if (!this.hovered) {
            return
        }
        this.hovered.onHoverLeave?.()
        this.onHoverLeave?.(this.hovered)
        this.hovered = null
    }

    /** 游標移動處理：純 Hover 檢測 (拖曳滑動已改由 Carousel/Detail 各自的 Lenis 實例處理) */
    _handlePointerMove(e) {
        this._updateNormalized(e.clientX, e.clientY)
        this._refreshHover()
    }

    /** 點擊處理共用邏輯：嚴格限定只有點擊 Canvas 本身時才生效 */
    _processTap(clientX, clientY, target) {
        if (target !== this.producer.canvas) {
            return
        }

        this._updateNormalized(clientX, clientY)
        const owner = this._raycastOwner()
        if (!owner) {
            return
        }

        owner.onClick?.()
        this.onClick?.(owner)
    }

    /** 滑鼠點擊處理：走瀏覽器原生 click 合成 */
    _handleClick(e) {
        // 觸控裝置若瀏覽器仍正常合成 click，避免與 _handlePointerUp 的判定重複觸發
        if (this._suppressNextClick) {
            this._suppressNextClick = false
            return
        }
        this._processTap(e.clientX, e.clientY, e.target)
    }

    _handlePointerDown(e) {
        if (e.pointerType !== 'touch' || e.target !== this.producer.canvas) {
            return
        }
        this._pointerDownInfo = { x: e.clientX, y: e.clientY, time: performance.now() }
    }

    /** 觸控放開處理：位移與時間都在門檻內才視為輕觸 */
    _handlePointerUp(e) {
        const start = this._pointerDownInfo
        this._pointerDownInfo = null

        if (e.pointerType !== 'touch' || !start) {
            return
        }

        const dist = Math.hypot(e.clientX - start.x, e.clientY - start.y)
        const duration = performance.now() - start.time
        if (dist > TAP_MAX_DIST || duration > TAP_MAX_DURATION) {
            return
        }

        this._suppressNextClick = true
        this._processTap(e.clientX, e.clientY, e.target)
    }

    /** 綁定全域 DOM 事件 */
    _setEventListeners() {
        document.addEventListener('mouseenter', this._onMouseEnter)
        document.addEventListener('mouseleave', this._onMouseLeave)
        window.addEventListener('pointermove', this._onPointerMove)
        window.addEventListener('click', this._onClick)
        window.addEventListener('pointerdown', this._onPointerDown)
        window.addEventListener('pointerup', this._onPointerUp)
    }
}
